import React, { useState, memo } from "react";
import { Box, Card, CardContent, useTheme } from "@mui/material";
import GridViewCard from "./GridViewCard";
import EmptyState from "./EmptyState";
import DocumentIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Document.js";
import type { PolicyDocument } from "../types/api";
import { downloadPolicyDocumentComplete } from "../services/policyDownloadService";
import type { PolicyDownloadRequest } from "../services/policyDownloadService";
import DownloadIndicator from "./DownloadIndicator";

interface PolicyGridViewProps {
  policies: PolicyDocument[];
  loading?: boolean;
  onSendEmail: (policyId: number, policyName: string) => void;
  onPolicyDownload?: (policy: PolicyDocument) => void;
  agencyId: string;
}

interface PolicyCardProps {
  policy: PolicyDocument;
  onSendEmail: (policyId: number, policyName: string) => void;
  onPolicyDownload?: (policy: PolicyDocument) => void;
  agencyId: string;
}

// OPTIMIZED: Memoized to prevent unnecessary re-renders
const PolicyCard = memo(function PolicyCard({
  policy,
  onSendEmail,
  onPolicyDownload,
  agencyId,
}: PolicyCardProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  // Handle direct download (used by menu action)
  const handleDirectDownload = async () => {
    try {
      setIsDownloading(true);

      const downloadRequest: PolicyDownloadRequest = {
        agencyId: agencyId,
        platform_org_id: policy.agency.platform_org_id,
        policyDocument: {
          id: policy.id.toString(),
          name: policy.name,
        },
        latestVersion: {
          versionNumber: policy.version_number || "1.0",
        },
        languageCode: policy.language || "EN",
      };

      await downloadPolicyDocumentComplete(downloadRequest);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle title click (prefer parent handler if available)
  const handleTitleClick = () => {
    if (onPolicyDownload) {
      // Use parent's download handler for consistency
      onPolicyDownload(policy);
    } else {
      // Fallback to direct download if no parent handler
      handleDirectDownload();
    }
  };

  const handleSendEmail = () => {
    onSendEmail(policy.id, policy.name);
  };

  const menuItems = [
    {
      label: isDownloading ? "Downloading..." : "Download",
      onClick: handleDirectDownload,
      disabled: isDownloading,
    },
    {
      label: "Send Email",
      onClick: handleSendEmail,
    },
  ];

  return (
    <Box sx={{ position: "relative" }}>
      <GridViewCard
        title={policy.name}
        updateDate={policy.updated_date}
        onClick={handleTitleClick}
        menuItems={menuItems}
        TitleIcon={DocumentIcon}
      />
      {isDownloading && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 12,
            backgroundColor: "background.paper",
            opacity: 0.9,
            borderRadius: 1,
            px: 1,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            boxShadow: 1,
          }}
        >
          <DownloadIndicator size={16} />
        </Box>
      )}
    </Box>
  );
});

export const PolicyGridView: React.FC<PolicyGridViewProps> = ({
  policies,
  loading = false,
  onSendEmail,
  onPolicyDownload,
  agencyId,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        {/* Loading placeholder cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
            gap: {
              xs: 2,  // 16px gap on mobile
              sm: 2.5, // 20px gap on small screens
              md: 3,   // 24px gap on medium+
            },
            mb: 4,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {[...Array(6)].map((_, index) => (
            <Card
              key={index}
              sx={{
                height: theme.spacing(37.5), // 300px equivalent
                backgroundColor: theme.palette.grey[100],
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                  }}
                >
                  Loading...
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  if (policies.length === 0) {
    return (
      <EmptyState title="No items to display." subtitle="No policies yet" />
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Grid of policy cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
          gap: {
            xs: 2,  // 16px gap on mobile
            sm: 2.5, // 20px gap on small screens
            md: 3,   // 24px gap on medium+
          },
          mb: 4,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {policies.map((policy, index) => (
          <PolicyCard
            key={`${policy.id}-${index}`}
            policy={policy}
            onSendEmail={onSendEmail}
            onPolicyDownload={onPolicyDownload}
            agencyId={agencyId}
          />
        ))}
      </Box>
    </Box>
  );
};

export default PolicyGridView;
